/**
 * QRAccessScanner - Scan QR codes to view delegate access permissions
 * Full-featured scanner with permission display overlay
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Camera, X, RefreshCw, Loader2, Shield, QrCode,
  SwitchCamera, Flashlight, FlashlightOff, Volume2, VolumeX
} from 'lucide-react';
import QRAccessDisplay from './QRAccessDisplay';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QRAccessScanner = ({ eventId, onClose }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [accessData, setAccessData] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [error, setError] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [activeCamera, setActiveCamera] = useState(null);
  const [torchOn, setTorchOn] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [scanCount, setScanCount] = useState(0);
  
  const html5QrRef = useRef(null);
  const audioSuccessRef = useRef(null);
  const audioErrorRef = useRef(null);

  // Initialize audio
  useEffect(() => {
    audioSuccessRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleAsYU8C+tmZGPGB6qcuyaCADQ5zX1rhNECpQmtDXsVQQJE2QzNmyUxAtT5DL1LFRDixPkMzVslMQKkuNy9SwUw8rT5DN1bNTDypLjcvUsFMPK06QzNWyUw8qS43L1LBTD');
    audioErrorRef.current = new Audio('data:audio/wav;base64,UklGRl9vT19teleASYU8C+tmZGPGB6');
  }, []);

  const playSound = (success) => {
    if (!soundEnabled) return;
    try {
      const audio = success ? audioSuccessRef.current : audioErrorRef.current;
      audio?.play().catch(() => {});
    } catch (e) {}
  };

  // Get cameras on mount
  useEffect(() => {
    Html5Qrcode.getCameras().then(devices => {
      setCameras(devices);
      // Prefer back camera
      const backCamera = devices.find(d => 
        d.label.toLowerCase().includes('back') || 
        d.label.toLowerCase().includes('rear')
      );
      setActiveCamera(backCamera?.id || devices[0]?.id);
    }).catch(err => {
      console.error('Error getting cameras:', err);
      setError('Unable to access camera. Please check permissions.');
    });

    return () => {
      stopScanning();
    };
  }, []);

  const startScanning = async () => {
    if (!activeCamera) {
      setError('No camera available');
      return;
    }

    setIsScanning(true);
    setError(null);
    setAccessData(null);
    setShowResults(false);

    try {
      html5QrRef.current = new Html5Qrcode('qr-reader-access');
      
      await html5QrRef.current.start(
        activeCamera,
        {
          fps: 15,
          qrbox: { width: 280, height: 280 },
          aspectRatio: 1.0,
        },
        onScanSuccess,
        () => {} // Ignore no QR in frame
      );
    } catch (err) {
      console.error('Failed to start scanner:', err);
      setError('Failed to start camera. Please try again.');
      setIsScanning(false);
    }
  };

  const stopScanning = async () => {
    if (html5QrRef.current) {
      try {
        await html5QrRef.current.stop();
        html5QrRef.current = null;
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
    }
    setIsScanning(false);
  };

  const onScanSuccess = useCallback(async (decodedText) => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    
    // Pause scanner
    if (html5QrRef.current) {
      try {
        await html5QrRef.current.pause();
      } catch (e) {}
    }

    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/admin/events/${eventId}/scan-access`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ qr_code: decodedText })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        playSound(true);
        setAccessData(data);
        setShowResults(true);
        setScanCount(prev => prev + 1);
        toast.success(`Access verified for ${data.delegate.name}`);
      } else {
        playSound(false);
        setError(data.detail || 'Invalid QR code');
        toast.error(data.detail || 'Scan failed');
        // Resume scanning after error
        setTimeout(() => {
          if (html5QrRef.current) {
            try {
              html5QrRef.current.resume();
            } catch (e) {}
          }
          setError(null);
        }, 2000);
      }
    } catch (err) {
      playSound(false);
      setError('Network error. Please try again.');
      toast.error('Network error');
      // Resume scanning
      setTimeout(() => {
        if (html5QrRef.current) {
          try {
            html5QrRef.current.resume();
          } catch (e) {}
        }
        setError(null);
      }, 2000);
    }

    setIsProcessing(false);
  }, [eventId, isProcessing]);

  const handleScanAnother = () => {
    setShowResults(false);
    setAccessData(null);
    // Resume scanning
    if (html5QrRef.current) {
      try {
        html5QrRef.current.resume();
      } catch (e) {}
    }
  };

  const toggleTorch = async () => {
    if (html5QrRef.current) {
      try {
        const track = html5QrRef.current.getRunningTrackCapabilities?.();
        if (track?.torch) {
          await html5QrRef.current.applyVideoConstraints({
            advanced: [{ torch: !torchOn }]
          });
          setTorchOn(!torchOn);
        }
      } catch (e) {
        toast.error('Flashlight not available');
      }
    }
  };

  const switchCamera = async () => {
    if (cameras.length < 2) return;
    
    await stopScanning();
    
    const currentIndex = cameras.findIndex(c => c.id === activeCamera);
    const nextIndex = (currentIndex + 1) % cameras.length;
    setActiveCamera(cameras[nextIndex].id);
    
    setTimeout(() => startScanning(), 500);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="qr-access-scanner">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-black/80 backdrop-blur-sm">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-blue-400" />
          <span className="text-white font-medium">Access Verification</span>
          {scanCount > 0 && (
            <Badge className="bg-blue-500">{scanCount} scanned</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/20"
            onClick={() => setSoundEnabled(!soundEnabled)}
          >
            {soundEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/20"
            onClick={onClose}
          >
            <X className="h-6 w-6" />
          </Button>
        </div>
      </div>

      {/* Camera View or Results */}
      <div className="flex-1 relative flex items-center justify-center overflow-hidden">
        {showResults && accessData ? (
          <div className="w-full h-full overflow-auto bg-gray-50 p-4">
            <QRAccessDisplay accessData={accessData} />
            <div className="mt-4 flex gap-2 justify-center sticky bottom-4">
              <Button 
                onClick={handleScanAnother}
                className="bg-blue-500 hover:bg-blue-600"
              >
                <QrCode className="h-4 w-4 mr-2" />
                Scan Another
              </Button>
              <Button 
                variant="outline"
                onClick={onClose}
              >
                Done
              </Button>
            </div>
          </div>
        ) : isScanning ? (
          <>
            <div id="qr-reader-access" className="w-full h-full" />
            
            {/* Scanning overlay */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative w-72 h-72">
                  {/* Corner brackets */}
                  <div className="absolute top-0 left-0 w-12 h-12 border-t-4 border-l-4 border-blue-400 rounded-tl-lg" />
                  <div className="absolute top-0 right-0 w-12 h-12 border-t-4 border-r-4 border-blue-400 rounded-tr-lg" />
                  <div className="absolute bottom-0 left-0 w-12 h-12 border-b-4 border-l-4 border-blue-400 rounded-bl-lg" />
                  <div className="absolute bottom-0 right-0 w-12 h-12 border-b-4 border-r-4 border-blue-400 rounded-br-lg" />
                  
                  {/* Scanning line */}
                  <div className="absolute inset-x-4 h-0.5 bg-blue-400 animate-scan-line" />
                </div>
              </div>
              
              {/* Dim outside */}
              <div className="absolute inset-0 bg-black/40" style={{
                clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 0, calc(50% - 144px) calc(50% - 144px), calc(50% - 144px) calc(50% + 144px), calc(50% + 144px) calc(50% + 144px), calc(50% + 144px) calc(50% - 144px), calc(50% - 144px) calc(50% - 144px))'
              }} />
            </div>

            {/* Processing overlay */}
            {isProcessing && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <div className="text-center text-white">
                  <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4" />
                  <p>Verifying access...</p>
                </div>
              </div>
            )}

            {/* Error overlay */}
            {error && !isProcessing && (
              <div className="absolute inset-0 bg-red-500/80 flex items-center justify-center">
                <div className="text-center text-white p-6">
                  <X className="h-16 w-16 mx-auto mb-4" />
                  <p className="text-xl font-bold">{error}</p>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center text-white p-8">
            <Camera className="h-24 w-24 mx-auto mb-6 opacity-50" />
            <p className="text-lg mb-6 opacity-70">
              {error || 'Scan badge QR code to verify access permissions'}
            </p>
            <Button
              size="lg"
              onClick={startScanning}
              className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-6 text-lg"
              disabled={!activeCamera}
            >
              <Shield className="h-6 w-6 mr-2" />
              Start Access Scan
            </Button>
          </div>
        )}
      </div>

      {/* Bottom Controls */}
      {isScanning && !showResults && (
        <div className="flex justify-center gap-4 p-6 bg-black/80 backdrop-blur-sm">
          <Button
            variant="outline"
            size="lg"
            className="bg-white/10 border-white/30 text-white hover:bg-white/20"
            onClick={toggleTorch}
          >
            {torchOn ? <FlashlightOff className="h-5 w-5" /> : <Flashlight className="h-5 w-5" />}
          </Button>
          
          {cameras.length > 1 && (
            <Button
              variant="outline"
              size="lg"
              className="bg-white/10 border-white/30 text-white hover:bg-white/20"
              onClick={switchCamera}
            >
              <SwitchCamera className="h-5 w-5" />
            </Button>
          )}
          
          <Button
            variant="outline"
            size="lg"
            className="bg-red-500/80 border-red-400 text-white hover:bg-red-600"
            onClick={stopScanning}
          >
            <X className="h-5 w-5 mr-2" />
            Stop
          </Button>
        </div>
      )}

      {/* CSS for animation */}
      <style jsx>{`
        @keyframes scan-line {
          0%, 100% { top: 10%; }
          50% { top: 90%; }
        }
        .animate-scan-line {
          animation: scan-line 2s ease-in-out infinite;
        }
        #qr-reader-access video {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
        }
      `}</style>
    </div>
  );
};

export default QRAccessScanner;
